/**
 * EAA-1810 (G8): the embed origin allowlist.
 *
 * Every `/embed/*` and `/h5p/*` page talks to its host page over `postMessage`.
 * Until now each of them posted with `targetOrigin: '*'` and accepted inbound
 * messages without checking `event.origin` at all — so embed payloads were
 * broadcast to whatever framed the page, and any framing page could drive the
 * editor. Nothing in `apps/editor/src` performed an origin check.
 *
 * That was survivable only because `nginx.conf` sent `X-Frame-Options: SAMEORIGIN`,
 * which blocked cross-origin framing outright — and which also blocked the
 * documented use case in `pages/embed/README.md` ("Embedding Content Editor in
 * External Applications"). Opening that up without an allowlist would turn a
 * latent hole into a live one, so the two changes ship together.
 *
 * **The default is same-origin only.** With `VITE_EMBED_ALLOWED_ORIGINS` unset,
 * behaviour is exactly what `X-Frame-Options: SAMEORIGIN` already permitted, so
 * existing editor/player/multi/H5P and LMS flows are unaffected (risk R4).
 *
 * Configure per environment, comma-separated:
 *   VITE_EMBED_ALLOWED_ORIGINS=https://partner.example.edu.vn,https://lms.vmb.edu.vn
 *
 * The end state is for this list to be derived from the portal app registry
 * (`Application.embed_origins`) rather than baked in at build time — see G8 in
 * `emg-portal-be/docs/apps-and-roles/PLAN-External-App-Onboarding-Gaps-Backend.md`.
 * That needs a registry read at deploy time; the env var is the shippable step.
 */

/** Normalise any URL-ish string to a bare origin, or '' if it is not one. */
function toOrigin(value: string): string {
    try {
        return new URL(value.trim()).origin;
    } catch {
        return '';
    }
}

let cached: readonly string[] | null = null;
let cachedAllowAll: boolean | null = null;

/**
 * EAA-1810: `VITE_EMBED_ALLOWED_ORIGINS=*` trusts ANY parent origin.
 *
 * Added 2026-08-06 on request, because the per-environment host lists were the
 * thing nobody kept up to date: dev broke when the LMS origin was missing, and
 * uat/prod would have broken the same way on their own hostnames.
 *
 * What this does and does not give up:
 *   - It DOES accept inbound messages from any framing site, and lets any site
 *     drive the embed. That is the real relaxation; only use it where the embed
 *     surface is not sensitive.
 *   - It does NOT reintroduce `postMessage(msg, '*')`. Even wide open, we post to
 *     the CONCRETE referrer origin (see `parentTargetOrigin`), so a message is
 *     never broadcast to an origin other than the one that framed us — the defect
 *     this module was written to remove stays removed.
 *
 * `EMBED_FRAME_ANCESTORS` (runtime, nginx) must be opened separately — this flag
 * only governs messaging, not whether the browser renders the frame.
 */
function parseConfig(): { origins: readonly string[]; allowAll: boolean } {
    const raw = (import.meta.env.VITE_EMBED_ALLOWED_ORIGINS ?? '').split(',').map((v) => v.trim());
    const allowAll = raw.includes('*');
    const configured = raw.map(toOrigin).filter(Boolean);
    return { origins: Array.from(new Set([window.location.origin, ...configured])), allowAll };
}

/** True when the allowlist is the `*` wildcard — any parent origin is trusted. */
export function allowsAnyOrigin(): boolean {
    if (cachedAllowAll === null) {
        const { origins, allowAll } = parseConfig();
        cached = origins;
        cachedAllowAll = allowAll;
    }
    return cachedAllowAll;
}

/**
 * Allowlisted parent origins. Always includes this page's own origin, so a
 * same-origin embed needs no configuration. Under the `*` wildcard this list is
 * still returned (own origin plus anything explicitly named), but
 * `isAllowedOrigin` stops consulting it — check `allowsAnyOrigin()` first.
 */
export function allowedOrigins(): readonly string[] {
    if (cached) return cached;
    const { origins, allowAll } = parseConfig();
    cached = origins;
    cachedAllowAll = allowAll;
    return cached;
}

/** Test seam — the module caches, and each case needs its own allowlist. */
export function __resetAllowedOriginsCache(): void {
    cached = null;
    cachedAllowAll = null;
}

export function isAllowedOrigin(origin: string): boolean {
    if (!origin || origin === 'null') return false; // sandboxed/opaque origin
    // An opaque origin is still refused under `*`: there is no origin to post back
    // to, so trusting it would mean broadcasting.
    if (allowsAnyOrigin()) return true;
    return allowedOrigins().includes(origin);
}

/**
 * The origin to post to. Derived from `document.referrer` — the host that framed
 * us — because `window.parent.origin` is unreadable cross-origin by design.
 *
 * Returns '' when the referrer is absent or not allowlisted. Callers MUST treat
 * '' as "do not post": falling back to '*' is the exact defect this replaces.
 */
export function parentTargetOrigin(): string {
    const referrer = toOrigin(document.referrer);
    if (referrer && isAllowedOrigin(referrer)) return referrer;
    // No referrer at all: same-origin embeds and direct navigation both land here.
    if (!referrer) return window.location.origin;
    return '';
}

/**
 * Post to the host page, or drop the message if its origin is not allowlisted.
 * Returns whether it was sent, so callers can log a drop rather than assume
 * delivery.
 */
export function postToParent(message: unknown): boolean {
    const targetOrigin = parentTargetOrigin();
    if (!targetOrigin) {
        console.warn(
            '[embed] dropped an outbound message: %s is not in VITE_EMBED_ALLOWED_ORIGINS'
                + ' (set it to a comma-separated list, or to * to trust any parent)',
            toOrigin(document.referrer) || '(unknown origin)',
        );
        return false;
    }
    window.parent?.postMessage(message, targetOrigin);
    return true;
}

/**
 * Guard for an inbound `message` handler. Returns true when the event should be
 * processed.
 *
 * Note this checks `event.origin`, never the message body: a hostile framer can
 * put any `source`/`type` it likes in the payload, so shape checks are not a
 * substitute for an origin check.
 */
export function isTrustedMessage(event: MessageEvent): boolean {
    return isAllowedOrigin(event.origin);
}
